import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Edit, Plus, RefreshCw, Search, Trash2, Filter, MoreVertical, Eye, Calendar, FileText, X, User, Users, Download } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const PersonnelSanctions = () => {
  const navigate = useNavigate();
  const { filialeId, currentFiliale, isConsolidated } = useFiliale();
  const [sanctions, setSanctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [formData, setFormData] = useState({
    personnel_id: '',
    personnel_nom: '',
    type_sanction_id: '',
    motif_sanction: '',
    date_debut: '',
    date_fin: ''
  });

  // États pour les filtres
  const [typesSanctions, setTypesSanctions] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);
  const [filters, setFilters] = useState({
    type_sanction_id: '',
    periode_deb: '',
    periode_fin: ''
  });

  // États pour les statistiques
  const [stats, setStats] = useState({
    miseAPied: 0,
    avertissement: 0,
    licenciement: 0
  });

  useEffect(() => {
    setPage(1);
  }, [filialeId, searchTerm, filters]);

  // Charger les types de sanctions
  useEffect(() => {
    const fetchTypesSanctions = async () => {
      try {
        const response = await http.get('/api/type-sanction');
        setTypesSanctions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erreur chargement types sanctions:', error);
        setTypesSanctions([]);
      }
    };
    fetchTypesSanctions();
  }, []);

  // Charger la liste du personnel (respecte le contexte filiale)
  useEffect(() => {
    const fetchPersonnel = async () => {
      try {
        const params = { limit: 1000 };
        if (!isConsolidated && filialeId) {
          params.filiale_id = filialeId;
        }
        const response = await http.get('/api/personnel', { params });
        setPersonnelList(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (error) {
        console.error('Erreur chargement personnel:', error);
        setPersonnelList([]);
      }
    };
    fetchPersonnel();
  }, [filialeId, isConsolidated]);

  // Charger les statistiques
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await http.get('/api/personnel-sanctions/stats/count');
        setStats(response.data);
      } catch (error) {
        console.error('Erreur chargement statistiques:', error);
      }
    };
    fetchStats();
  }, [refreshKey]);

  useEffect(() => {
    const fetchSanctions = async () => {
      try {
        setLoading(true);
        const params = { page, limit: 20, search: searchTerm };
        if (filters.type_sanction_id) params.type_sanction_id = filters.type_sanction_id;
        if (filters.periode_deb) params.periode_deb = filters.periode_deb;
        if (filters.periode_fin) params.periode_fin = filters.periode_fin;

        const response = await http.get('/api/personnel-sanctions', { params });
        setSanctions(response.data.data || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        setSanctions([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchSanctions();
  }, [page, searchTerm, filters, refreshKey]);

  const handleCreate = () => {
    setEditData(null);
    setFormData({
      personnel_id: '',
      personnel_nom: '',
      type_sanction_id: '',
      motif_sanction: '',
      date_debut: '',
      date_fin: ''
    });
    setShowModal(true);
  };

  const handleEdit = (sanction) => {
    setEditData(sanction);
    setFormData({
      personnel_id: sanction.personnel_id,
      personnel_nom: `${sanction.prenom} ${sanction.nom}`,
      type_sanction_id: sanction.type_sanction_id,
      motif_sanction: sanction.motif_sanction,
      date_debut: sanction.date_debut,
      date_fin: sanction.date_fin
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette sanction ?')) return;

    try {
      await http.delete(`/api/personnel-sanctions/${id}`);
      setRefreshKey(prev => prev + 1);
      setPage(1);
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handlePreview = async (sanction) => {
    try {
      const response = await http.get(`/api/personnel-sanctions/${sanction.idpersonnel_sanction}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fiche-sanction-${sanction.idpersonnel_sanction}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const handleSelectPersonnel = (person) => {
    setFormData({
      ...formData,
      personnel_id: person.idpersonnel,
      personnel_nom: `${person.prenom} ${person.nom}`
    });
    setShowPersonnelModal(false);
    setPersonnelSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('personnel_id', formData.personnel_id);
      formDataToSend.append('type_sanction_id', formData.type_sanction_id);
      formDataToSend.append('motif_sanction', formData.motif_sanction);
      formDataToSend.append('date_debut', formData.date_debut);
      formDataToSend.append('date_fin', formData.date_fin);

      const fileInput = e.target.querySelector('input[type="file"]');
      if (fileInput && fileInput.files[0]) {
        formDataToSend.append('sanction_scanne', fileInput.files[0]);
      }

      if (editData) {
        await http.put(`/api/personnel-sanctions/${editData.idpersonnel_sanction}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await http.post('/api/personnel-sanctions', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setShowModal(false);
      setRefreshKey(prev => prev + 1);
      setPage(1);
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Sanctions du personnel"
        subtitle="Gestion des sanctions disciplinaires"
        actions={[
          {
            label: 'Créer',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: () => setPage(1),
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
        {/* Statistiques */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Mise à pied</p>
                <p className="text-2xl font-bold text-gray-900">{stats.miseAPied}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avertissement</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avertissement}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Licenciement</p>
              <p className="text-2xl font-bold text-gray-900">{stats.licenciement}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.miseAPied + stats.avertissement + stats.licenciement}</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, prénom..."
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 pl-10 pr-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Type de sanction</label>
            <select
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
              value={filters.type_sanction_id}
              onChange={(e) => setFilters({ ...filters, type_sanction_id: e.target.value })}
            >
              <option value="">Tous</option>
              {typesSanctions.map((type) => (
                <option key={type.idtype_sanction} value={type.idtype_sanction}>
                  {type.libelle_sanction}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[150px]">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Date début</label>
            <input
              type="date"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
              value={filters.periode_deb}
              onChange={(e) => setFilters({ ...filters, periode_deb: e.target.value })}
            />
          </div>
          <div className="min-w-[150px]">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Date fin</label>
            <input
              type="date"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
              value={filters.periode_fin}
              onChange={(e) => setFilters({ ...filters, periode_fin: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            Chargement…
          </div>
        ) : sanctions.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            Aucune sanction trouvée.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employé</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Motif</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date début</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date fin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Document</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sanctions.map((sanction) => (
                  <tr key={sanction.idpersonnel_sanction} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {sanction.prenom} {sanction.nom}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {sanction.libelle_sanction}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {sanction.motif_sanction}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(sanction.date_debut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(sanction.date_fin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {sanction.fiche_pdf || sanction.sanction_scanne ? (
                        <button
                          type="button"
                          onClick={() => handlePreview(sanction)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(sanction)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(sanction.idpersonnel_sanction)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} sur {Math.ceil(total / 20)}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                type="button"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / 20)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de création/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl border border-blue-100">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4">
              <h3 className="text-lg font-bold text-blue-900">
                {editData ? 'Modifier' : 'Créer'} une sanction
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-900"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Employé
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      placeholder="Sélectionner un employé"
                      className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white bg-gray-50 dark:bg-gray-600"
                      value={formData.personnel_nom}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPersonnelModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <User className="h-4 w-4" />
                      Choisir
                    </button>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Type de sanction
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                    value={formData.type_sanction_id}
                    onChange={(e) => setFormData({ ...formData, type_sanction_id: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {typesSanctions.map((type) => (
                      <option key={type.idtype_sanction} value={type.idtype_sanction}>
                        {type.libelle_sanction}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Motif
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                    rows="3"
                    value={formData.motif_sanction}
                    onChange={(e) => setFormData({ ...formData, motif_sanction: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Date début
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Date fin
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3 border-t border-blue-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editData ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de sélection d'employé */}
      {showPersonnelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4">
              <h3 className="text-lg font-bold text-gray-900">Sélectionner un employé</h3>
              <button
                type="button"
                onClick={() => setShowPersonnelModal(false)}
                className="text-gray-500 hover:text-gray-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un employé..."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 pl-10 pr-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                  value={personnelSearch}
                  onChange={(e) => setPersonnelSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {personnelList
                .filter(person =>
                  !personnelSearch ||
                  person.nom.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                  person.prenom.toLowerCase().includes(personnelSearch.toLowerCase())
                )
                .map((person) => (
                  <button
                    key={person.idpersonnel}
                    type="button"
                    onClick={() => handleSelectPersonnel(person)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {person.photo_profil ? (
                      <img
                        src={person.photo_profil}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                        <User className="h-5 w-5" />
                      </div>
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {person.prenom} {person.nom}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {person.titre_libelle || 'Non défini'}
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PersonnelSanctions;
